import { Cancel, CheckCircle, OpenInNew } from "@mui/icons-material";
import { Button, Chip, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ApplicationReviewDialogs from "./ApplicationReviewDialogs";
import { fetchClientApplicationRequests } from "./referralApplicationApi";

const MODULE_LABELS = {
  admission: "Admission",
  visa: "Visa",
  insurance: "Insurance",
  "skill-assessment": "Skill Assessment",
  "rpl-course": "RPL",
  other: "Other",
};

const MODULE_EDIT_PATH = {
  admission: (id) => `/admin/admission/edit/${id}`,
  visa: (id) => `/admin/visa/edit/${id}`,
  insurance: (id) => `/admin/insurance/edit/${id}`,
  "skill-assessment": (id) => `/admin/skill-assessment/edit/${id}`,
  "rpl-course": (id) => `/admin/rpl-client/edit/${id}`,
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "dd MMM yyyy, HH:mm");
};

const DetailEntries = ({ details }) => {
  const entries = Object.entries(details || {}).filter(
    ([key, value]) =>
      // Technical keys (courseId, …) travel as themselves, not as display lines.
      !/id$/i.test(key) && value !== null && value !== undefined && String(value).trim() !== ""
  );
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 m-0 mt-2">
      {entries.map(([key, value]) => (
        <div key={key} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-app-text-muted font-semibold">
            {key}
          </dt>
          <dd className="m-0 mt-0.5 text-sm text-app-text break-words">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
};

/**
 * Partner application requests for one client, shown at the top of the admin Applications tab.
 * Approving creates the real module entry with the partner's details filled in; rejecting returns
 * the request with a reason the partner can act on. Renders nothing when there are no requests,
 * so non-referral clients never see it.
 */
const ReferralApplicationSection = ({ clientId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canRead = usePermission(P.REFERRAL.APPLICATION_READ);
  const canReview = usePermission(P.REFERRAL.APPLICATION_REVIEW);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["referralApplicationRequests", clientId],
    queryFn: () => fetchClientApplicationRequests(clientId),
    enabled: Boolean(clientId) && canRead,
    refetchOnWindowFocus: false,
  });

  if (!canRead || (!isLoading && requests.length === 0)) return null;

  const pending = requests.filter((row) => row?.status === "PENDING");
  const decided = requests.filter((row) => row?.status !== "PENDING");

  const refresh = () => {
    queryClient.invalidateQueries(["referralApplicationRequests", clientId]);
    queryClient.invalidateQueries(["clientApplications", clientId]);
  };

  const closeDialogs = () => {
    setApproving(null);
    setRejecting(null);
  };

  const openEntry = (row) => {
    const path = MODULE_EDIT_PATH[row?.linkedModuleType]?.(row?.linkedModuleId);
    if (path) navigate(path);
  };

  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Typography variant="subtitle2" className="!font-semibold text-amber-900">
          Referral application requests
          {pending.length > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`${pending.length} pending`}
              className="!ml-2"
            />
          )}
        </Typography>
        {decided.length > 0 && pending.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {decided.length} reviewed
          </Typography>
        )}
      </div>

      {isLoading && (
        <Typography variant="body2" color="text.secondary" className="!mt-2">
          Loading requests…
        </Typography>
      )}

      <div className="mt-3 space-y-3">
        {pending.map((row) => (
          <div key={row.id} className="rounded-md border border-amber-200 bg-white p-3">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Chip
                    size="small"
                    color="primary"
                    label={MODULE_LABELS[row.moduleType] || row.moduleType}
                  />
                  <span className="text-sm font-medium text-app-text">
                    Requested by {row.partnerName || "the referral partner"}
                  </span>
                </div>
                <p className="text-xs text-app-text-secondary m-0 mt-1">
                  {formatDateTime(row.createdAt)}
                  {row.createdByName ? ` · by ${row.createdByName}` : ""}
                </p>
                {row.requestNote && (
                  <p className="text-sm text-app-text m-0 mt-2 whitespace-pre-wrap">
                    {row.requestNote}
                  </p>
                )}
                <DetailEntries details={row.requestDetails} />
              </div>
              {canReview && (
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel fontSize="small" />}
                    onClick={() => setRejecting(row)}
                  >
                    Reject
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle fontSize="small" />}
                    onClick={() => setApproving(row)}
                  >
                    Approve
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}

        {decided.map((row) => (
          <div
            key={row.id}
            className="rounded-md border border-app-border bg-white px-3 py-2 flex items-center justify-between gap-2 flex-wrap"
          >
            <div className="min-w-0 flex items-center gap-2 flex-wrap">
              <Chip
                size="small"
                color={row.status === "APPROVED" ? "success" : "default"}
                label={MODULE_LABELS[row.moduleType] || row.moduleType}
              />
              <span className="text-xs text-app-text-secondary">
                {row.status === "APPROVED" ? "Approved" : "Returned"}
                {row.reviewedBy ? ` by ${row.reviewedBy}` : ""} · {formatDateTime(row.reviewedAt)}
              </span>
              {row.status === "REJECTED" && row.adminNote && (
                <span className="text-xs text-app-text truncate" title={row.adminNote}>
                  — {row.adminNote}
                </span>
              )}
            </div>
            {row.status === "APPROVED" && row.linkedModuleId && (
              <Button
                size="small"
                startIcon={<OpenInNew fontSize="small" />}
                onClick={() => openEntry(row)}
              >
                Open entry
              </Button>
            )}
          </div>
        ))}
      </div>

      <ApplicationReviewDialogs
        approving={approving}
        rejecting={rejecting}
        onClose={closeDialogs}
        onDone={refresh}
      />
    </div>
  );
};

export default ReferralApplicationSection;
